import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, retry, Subscription, throwError, timer } from 'rxjs';
import { IDispositivoAlarma } from 'modelos/src';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { PermisosEntidadesService } from '../../../../../auxiliares/servicios/http/permisos-entidades.service';

/** Chip del encabezado; `estado` le da el color semántico. */
interface ChipEncabezado {
  label: string;
  icon?: string;
  estado?: 'alerta';
}

@Component({
  selector: 'app-detalles-alarma',
  templateUrl: './detalles-alarma.component.html',
  styleUrl: '../../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesAlarmaComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  permisosEntidades = inject(PermisosEntidadesService);
  helper = inject(HelperService);

  public selectedTab = 0;
  public puedeEditar = this.helper.tienePermiso('Alarmas - Editar');
  public idAlarma = '';
  public alarma: IDispositivoAlarma;
  /** La alarma refrescada, para el encabezado (ver listar). */
  public encabezado?: IDispositivoAlarma;
  public loading = true;

  public alarma$: Subscription;

  public chips: ChipEncabezado[] = [];

  /** El domicilio identifica a la alarma: va de subtítulo. */
  public get direccion(): string | undefined {
    const d = this.encabezado?.domicilio?.direccion;
    return d ? HelperService.extraerDireccion(d) || undefined : undefined;
  }

  /**
   * Las pestañas en orden, por nombre: el query param `tab` va por nombre
   * (?tab=controlHorario) porque el índice cambia según las que se ocultan
   * por permiso (Cámaras, Logs Crudos). Tiene que coincidir con la plantilla.
   */
  private pestanas(): string[] {
    return [
      'info',
      'historialEventos',
      ...(this.permisosEntidades.verModuloCamaras() ? ['camaras'] : []),
      'eventosPersonalizados',
      ...(this.helper.tienePermiso('Logs - Ver logs crudos alarmas')
        ? ['logsCrudos']
        : []),
      'controlHorario',
      'notas',
      'contactos',
      'servicioTecnico',
      'asignaciones',
    ];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  public async listar(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.alarma$?.unsubscribe();
      // Cada 30 s, como Info: si la cuenta se suspende, se le da el alta o se
      // renombra, el encabezado se entera. Si la alarma no existe (se borró,
      // no hay acceso) el getById falla: sin el error la página quedaba con
      // el spinner para siempre. Un error en un refresco se reintenta.
      this.alarma$ = this.lista2
        .dispositivoAlarma(this.idAlarma, 30)
        .pipe(
          retry({
            delay: (error) => {
              resolve();
              return this.alarma ? timer(30000) : throwError(() => error);
            },
          }),
        )
        .subscribe({
          next: (data) => {
            // `alarma` se fija con la primera respuesta: las pestañas la
            // reciben de input y un objeto nuevo cada 30 s las recargaría
            // (las cámaras reiniciarían el video). El refresco solo
            // actualiza el encabezado.
            this.alarma ??= data;
            this.encabezado = data;
            this.setChips();
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  /**
   * Cliente, y lo que dice algo de la cuenta: sin alta o suspendida. El
   * número de abonado está en la tarjeta Equipo (con copiar).
   */
  public setChips() {
    const chips: ChipEncabezado[] = [];

    const a = this.encabezado;
    if (a?.cliente?.nombre) {
      chips.push({ label: a.cliente.nombre, icon: 'business' });
    }
    if (a && !a.fechaAlta) {
      chips.push({ label: 'Sin alta', icon: 'pending' });
    }
    if (a?.estadoCuenta === 'Suspendido') {
      chips.push({ label: 'Suspendida', icon: 'block', estado: 'alerta' });
    }

    this.chips = chips;
  }

  public editar(): void {
    this.router.navigate([
      'main/alarmas/alarmasDomiciliarias/editar',
      this.idAlarma,
    ]);
  }

  async ngOnInit(): Promise<void> {
    const params = await firstValueFrom(this.activatedRoute.paramMap);
    this.idAlarma = params.get('id');

    await this.listar();
    this.setChips();

    this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.alarma$?.unsubscribe();
  }
}
